import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Link, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getWebsiteUnrouted, dripErrorMessage,
  type WebsiteUnroutedPerson, type WebsiteUnroutedReport,
} from '../services/drip-api';
// The same reader as every other drip screen (these times already carry a Z, so they read as before).
import { formatServerTime as when } from '../services/server-time';

/**
 * Website inquiries that never made it into Verity.
 *
 * The website's contact form posts to Verity through laurusinquiry. When Verity refuses a
 * submission, or never confirms it, the person is left only in that app's lead table - nobody in
 * admissions hears about them. This tab is how they get found and contacted by hand.
 *
 * Read-only, and SIMS keeps no copy: the server reads the table when the tab opens. Phone numbers
 * appear here and nowhere else in SIMS.
 *
 * Layout tiers (standing rule - separate views, never one fluid rule): the base styles are the
 * laptop, with the reason summary above the table; from 1800px the summary becomes a side column;
 * from 2400px (the 34-inch ultrawide) that column and the table's spacing open up. Rules inside a
 * media query never reach the laptop, so tuning the ultrawide cannot regress it.
 */

const WIDE = '@media (min-width: 1800px)';
const ULTRAWIDE = '@media (min-width: 2400px)';

const LAYOUT = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 2,
  [WIDE]: { gridTemplateColumns: '260px minmax(0, 1fr)', gap: 3 },
  [ULTRAWIDE]: { gridTemplateColumns: '320px minmax(0, 1fr)', gap: 4 },
};

// A grid or flex child refuses to shrink below its content unless told to; without this one long
// email address brings back the sideways scroll the tiers exist to avoid.
const SHRINKS = { minWidth: 0 };

const SUMMARY_LIST = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  [WIDE]: { flexDirection: 'column', flexWrap: 'nowrap' },
};

// Headers take the same ultrawide padding as the cells below them, or each heading starts left of
// its column; and a size step above the cells, as on the laptop.
const HEAD = {
  color: 'var(--text-primary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  [ULTRAWIDE]: { px: 2.5, fontSize: '0.95rem' },
};
const CELL = {
  color: 'var(--text-secondary)',
  fontSize: '0.8rem',
  overflowWrap: 'anywhere',
  [ULTRAWIDE]: { fontSize: '0.9rem', px: 2.5, py: 1.25 },
};

/** Digits and a leading +, which is all a dialler needs. */
function telHref(phone: string): string | null {
  const dial = phone.replace(/[^\d+]/g, '');
  return /\d/.test(dial) ? `tel:${dial}` : null;
}

function PeopleTable({ people, showFilterReason }: {
  people: WebsiteUnroutedPerson[];
  showFilterReason?: boolean;
}) {
  return (
    <TableContainer component={Paper} sx={SHRINKS}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={HEAD}>Name</TableCell>
            <TableCell sx={HEAD}>Email</TableCell>
            <TableCell sx={HEAD}>Phone</TableCell>
            <TableCell sx={HEAD}>Program</TableCell>
            <TableCell sx={HEAD}>Location</TableCell>
            <TableCell sx={HEAD}>Reason</TableCell>
            {showFilterReason && <TableCell sx={HEAD}>Filtered because</TableCell>}
            <TableCell sx={HEAD} align="center">Attempts</TableCell>
            <TableCell sx={HEAD}>Last submitted</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {people.map((p, i) => {
            const tel = telHref(p.phone || '');
            return (
              // No record id leaves the server (RowKeys are withheld), so position is the key -
              // the list is replaced whole on every load, never edited in place.
              <TableRow key={i} hover>
                <TableCell sx={{ ...CELL, color: 'var(--text-primary)' }}>
                  {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                </TableCell>
                <TableCell sx={CELL}>{p.email || '—'}</TableCell>
                <TableCell sx={{ ...CELL, whiteSpace: 'nowrap', overflowWrap: 'normal' }}>
                  {tel ? <Link href={tel} sx={{ color: 'var(--link)' }}>{p.phone}</Link> : (p.phone || '—')}
                </TableCell>
                <TableCell sx={CELL}>{p.program || '—'}</TableCell>
                <TableCell sx={CELL}>{p.location || '—'}</TableCell>
                <TableCell sx={CELL}>{p.reason}</TableCell>
                {showFilterReason && <TableCell sx={CELL}>{p.filterReason || '—'}</TableCell>}
                <TableCell sx={CELL} align="center">{p.attempts}</TableCell>
                <TableCell sx={{ ...CELL, whiteSpace: 'nowrap', overflowWrap: 'normal' }}>
                  {when(p.lastSubmittedAt)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function WebsiteUnrouted() {
  const [report, setReport] = useState<WebsiteUnroutedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFiltered, setShowFiltered] = useState(false);

  const load = useCallback(async (refresh: boolean) => {
    setLoading(true); setError(null);
    try {
      setReport(await getWebsiteUnrouted(refresh));
    } catch (e) {
      // A failed read shows nothing rather than the last list: an old list under a new error
      // reads as current, and the server never sends a partial one.
      setReport(null);
      setError(dripErrorMessage(e, 'The website lead table could not be read. Try Refresh in a minute.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(false); }, [load]);

  const people = report?.people ?? [];
  const filtered = report?.filtered ?? [];
  const reasons = Object.entries(report?.counts.byReason ?? {});

  return (
    <Box sx={{ p: 3, [ULTRAWIDE]: { px: 5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={SHRINKS}>
          <Typography variant="h5" sx={{ color: 'var(--text-primary)' }}>Website Leads: not in Verity</Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 820 }}>
            People who sent the website inquiry form in the last {report?.windowDays ?? 90} days and
            never made it into Verity — Verity refused the submission or never confirmed it, so nobody
            was told about them. Contact them directly. Anyone Verity has accepted, at any time, is
            left out. Read from the website's lead table when this tab opens; SIMS keeps no copy.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} disabled={loading}
                  onClick={() => void load(true)}>
            Refresh
          </Button>
          {report && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--text-secondary)' }}>
              Checked {when(report.generatedAt)}
            </Typography>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1, color: 'var(--text-secondary)' }}>
            Reading the website lead table — this can take up to 20 seconds.
          </Typography>
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && report && (
        <Box sx={LAYOUT}>
          <Paper sx={{ ...SHRINKS, p: 2, alignSelf: 'start', [ULTRAWIDE]: { p: 3 } }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', mb: 1 }}>
              {people.length} {people.length === 1 ? 'person' : 'people'} to contact
            </Typography>
            <Box sx={SUMMARY_LIST}>
              {reasons.map(([reason, n]) => (
                <Chip key={reason} size="small" label={`${reason}: ${n}`}
                      sx={{ fontSize: '0.75rem', justifyContent: 'flex-start', maxWidth: '100%' }} />
              ))}
              {reasons.length === 0 && (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>None.</Typography>
              )}
            </Box>
          </Paper>

          <Box sx={SHRINKS}>
            {people.length === 0 ? (
              <Alert severity="success">
                Nobody from the website is missing from Verity in the last {report.windowDays} days.
              </Alert>
            ) : (
              <PeopleTable people={people} />
            )}

            {filtered.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Button size="small" onClick={() => setShowFiltered(!showFiltered)}
                        startIcon={showFiltered ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ color: 'var(--text-secondary)' }}>
                  {showFiltered ? 'Hide' : 'Show'} {filtered.length} filtered as likely spam
                </Button>
                <Collapse in={showFiltered} unmountOnExit>
                  <Typography variant="caption" sx={{ display: 'block', my: 1, color: 'var(--text-secondary)' }}>
                    Kept rather than dropped, so a real person caught by the filter can still be found.
                  </Typography>
                  <PeopleTable people={filtered} showFilterReason />
                </Collapse>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
